import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setFinalCanvasImage } from "../../redux/slices/uploadFlowSlice";
import { buildImageReadApiUrl, fetchImage } from "../../services/imageReadService";
import {
  ArtworkImageFit,
  ArtworkPlacement,
  isArtworkDisabledForProduct,
  resolveArtworkPlacement,
} from "../../config/productArtworkPlacements";
import { detectImageContentBounds } from "../../utils/imageContentBounds";
import {
  drawableHasTransparency,
  isTransparentHobbyBackgroundName,
  readFavouriteTransparencyFlag,
  stripWhiteMatteFromDrawable,
} from "../../services/caricatureHobbyService";

function shouldStripArtworkWhiteMatte(): boolean {
  if (readFavouriteTransparencyFlag()) return true;
  try {
    const stored = JSON.parse(
      localStorage.getItem("selectedCaricatureHobbyBackground") || "null"
    );
    return isTransparentHobbyBackgroundName(stored?.name);
  } catch {
    return false;
  }
}

/** Corner samples look like an opaque white studio plate. */
function artworkHasOpaqueWhiteCorners(image: HTMLImageElement): boolean {
  try {
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    if (!w || !h) return false;
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    const points = [
      [0, 0],
      [w - 1, 0],
      [0, h - 1],
      [w - 1, h - 1],
    ];
    let whiteCorners = 0;
    for (let i = 0; i < points.length; i += 1) {
      const [x, y] = points[i];
      ctx.clearRect(0, 0, 1, 1);
      ctx.drawImage(image, x, y, 1, 1, 0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      if (a > 200 && Math.min(r, g, b) >= 235) whiteCorners += 1;
    }
    return whiteCorners >= 3;
  } catch {
    return false;
  }
}

interface ProductArtworkPreviewProps {
  productImage?: string | null;
  artworkImage?: unknown;
  itemKey?: string;
  productName?: string | null;
  skuCode?: string | null;
  placement?: unknown;
  exportResult?: boolean;
  className?: string;
  style?: CSSProperties;
  onRendered?: (dataUrl: string | null) => void;
  /** Delay canvas image work until near viewport. */
  lazy?: boolean;
}

type DrawRect = { x: number; y: number; width: number; height: number };

function drawRectForFit(
  image: HTMLImageElement,
  bounds: DrawRect,
  fit: ArtworkImageFit
): DrawRect {
  if (fit === "stretch") return bounds;

  const imageAspect = image.naturalWidth / image.naturalHeight;
  if (fit === "frame" && imageAspect >= 0.88 && imageAspect <= 1.12) {
    return bounds;
  }
  if (fit === "height") {
    const width = bounds.height * imageAspect;
    return {
      x: bounds.x + (bounds.width - width) / 2,
      y: bounds.y,
      width,
      height: bounds.height,
    };
  }

  const boundsAspect = bounds.width / bounds.height;
  const useContain = fit === "contain" || fit === "frame";
  const fitByWidth = useContain
    ? imageAspect > boundsAspect
    : imageAspect < boundsAspect;

  if (fitByWidth) {
    const height = bounds.width / imageAspect;
    return {
      x: bounds.x,
      y: bounds.y + (bounds.height - height) / 2,
      width: bounds.width,
      height,
    };
  }

  const width = bounds.height * imageAspect;
  return {
    x: bounds.x + (bounds.width - width) / 2,
    y: bounds.y,
    width,
    height: bounds.height,
  };
}

function loadImageElement(
  url: string,
  crossOrigin?: string
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) image.crossOrigin = crossOrigin;
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    image.src = url;
  });
}

const artworkObjectUrls = new WeakMap<HTMLImageElement, string>();

async function loadImageFromBlobUrl(source: string): Promise<HTMLImageElement> {
  // Prefer a raw byte fetch so PNG alpha survives (some image proxies flatten to JPEG).
  const tryFetch = async (fetcher: () => Promise<Response>) => {
    const response = await fetcher();
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    // If the proxy flattened a PNG to JPEG, alpha is already gone — still try direct first.
    const objectUrl = URL.createObjectURL(blob);
    const image = await loadImageElement(objectUrl);
    artworkObjectUrls.set(image, objectUrl);
    return image;
  };

  try {
    return await tryFetch(() => fetch(source, { mode: "cors", credentials: "omit" }));
  } catch {
    return tryFetch(() => fetchImage(source));
  }
}

function releaseArtworkObjectUrl(image: HTMLImageElement | null) {
  if (!image) return;
  const objectUrl = artworkObjectUrls.get(image);
  if (!objectUrl) return;
  URL.revokeObjectURL(objectUrl);
  artworkObjectUrls.delete(image);
}

/**
 * Load product mockups via the image-read proxy (CORS-safe).
 * Artwork PNGs load via blob fetch first so alpha is not flattened to white.
 */
function loadImage(source: string, preferAlpha = false): Promise<HTMLImageElement> {
  const trimmed = (source || "").trim();
  if (!trimmed) return Promise.reject(new Error("Missing image source"));

  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
    return loadImageElement(trimmed);
  }

  if (preferAlpha) {
    return loadImageFromBlobUrl(trimmed).catch(() => {
      const proxied = buildImageReadApiUrl(trimmed);
      return loadImageElement(proxied, "anonymous").catch(() =>
        loadImageElement(trimmed, "anonymous")
      );
    });
  }

  const proxied = buildImageReadApiUrl(trimmed);
  const candidates = [
    { url: proxied, crossOrigin: "anonymous" },
    ...(proxied !== trimmed
      ? [
          { url: trimmed, crossOrigin: "anonymous" as const },
          { url: trimmed, crossOrigin: undefined },
        ]
      : []),
  ];

  return new Promise((resolve, reject) => {
    const tryCandidate = (index: number) => {
      const candidate = candidates[index];
      if (!candidate) {
        reject(new Error(`Failed to load image: ${source}`));
        return;
      }

      loadImageElement(candidate.url, candidate.crossOrigin)
        .then(resolve)
        .catch(() => tryCandidate(index + 1));
    };

    tryCandidate(0);
  });
}

function drawImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  bounds: DrawRect,
  fit: ArtworkImageFit
) {
  const rect = drawRectForFit(image, bounds, fit);
  ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  return rect;
}

/**
 * The print area is measured against the product itself, so mockups with more
 * surrounding whitespace still receive artwork of the same relative size.
 */
function resolveArtworkReference(
  product: HTMLImageElement,
  productRect: DrawRect,
  placement: ArtworkPlacement,
  fullCanvas: DrawRect
): DrawRect {
  if (placement.areaRelativeTo === "canvas") return fullCanvas;
  if (placement.areaRelativeTo === "product") return productRect;

  const bounds = detectImageContentBounds(product);
  return {
    x: productRect.x + bounds.x * productRect.width,
    y: productRect.y + bounds.y * productRect.height,
    width: bounds.width * productRect.width,
    height: bounds.height * productRect.height,
  };
}

function resolveArtworkDrawable(
  image: HTMLImageElement
): CanvasImageSource {
  // Transparent hobby art / white-plate PNGs: strip studio matte so
  // hoodies, tees, and totes never keep a white rectangle.
  if (drawableHasTransparency(image)) return image;

  const shouldStrip =
    shouldStripArtworkWhiteMatte() || artworkHasOpaqueWhiteCorners(image);
  if (!shouldStrip) return image;

  const stripped = stripWhiteMatteFromDrawable(
    image,
    image.naturalWidth,
    image.naturalHeight
  );
  return stripped || image;
}

function drawArtwork(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  placement: ArtworkPlacement,
  reference: DrawRect
) {
  const drawable = resolveArtworkDrawable(image);
  const artWidth =
    (drawable as HTMLCanvasElement).width ||
    image.naturalWidth ||
    image.width;
  const artHeight =
    (drawable as HTMLCanvasElement).height ||
    image.naturalHeight ||
    image.height;

  const bounds = {
    x: reference.x + placement.area.x * reference.width,
    y: reference.y + placement.area.y * reference.height,
    width: placement.area.width * reference.width,
    height: placement.area.height * reference.height,
  };
  const imageAspect = artWidth / Math.max(1, artHeight);
  const isSquareArt = imageAspect >= 0.88 && imageAspect <= 1.12;
  const isLandscapeArt = imageAspect > 1.12;
  const artScale = isLandscapeArt
    ? placement.artworkLandscapeScale || 1
    : isSquareArt
      ? placement.artworkSheetScale || 1
      : 1;
  const drawBounds = {
    x: bounds.x + (bounds.width * (1 - artScale)) / 2,
    y: bounds.y + (bounds.height * (1 - artScale)) / 2,
    width: bounds.width * artScale,
    height: bounds.height * artScale,
  };
  const fit =
    placement.artworkFit === "frame" ? "contain" : placement.artworkFit;
  const centerX = drawBounds.x + drawBounds.width / 2;
  const centerY = drawBounds.y + drawBounds.height / 2;

  // drawImage() helper expects HTMLImageElement natural sizes — draw manually for canvas sources.
  const target = drawRectForFit(
    {
      naturalWidth: artWidth,
      naturalHeight: artHeight,
    } as HTMLImageElement,
    {
      x: -drawBounds.width / 2,
      y: -drawBounds.height / 2,
      width: drawBounds.width,
      height: drawBounds.height,
    },
    fit
  );

  ctx.save();
  ctx.beginPath();
  const clip = artScale < 1 ? bounds : drawBounds;
  ctx.rect(clip.x, clip.y, clip.width, clip.height);
  ctx.clip();
  ctx.globalAlpha = placement.artworkOpacity;
  ctx.translate(centerX, centerY);
  ctx.rotate((placement.artworkRotation * Math.PI) / 180);
  ctx.drawImage(drawable, target.x, target.y, target.width, target.height);
  ctx.restore();
}

const ProductArtworkPreview = ({
  productImage,
  artworkImage,
  itemKey,
  productName,
  skuCode,
  placement: placementInput,
  exportResult = true,
  className,
  style,
  onRendered,
  lazy = true,
}: ProductArtworkPreviewProps) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dispatch = useDispatch();
  const [inView, setInView] = useState(!lazy);
  const [ready, setReady] = useState(false);
  const placement = useMemo(
    () => resolveArtworkPlacement(skuCode, placementInput, productName),
    [skuCode, placementInput, productName]
  );
  const artworkDisabled = useMemo(
    () => isArtworkDisabledForProduct(productName),
    [productName]
  );

  useEffect(() => {
    setReady(false);
  }, [productImage, artworkImage, itemKey]);

  useEffect(() => {
    if (!lazy || inView) return;
    const node = wrapRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      { rootMargin: "160px 0px", threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, lazy]);

  useEffect(() => {
    if (!inView) return;

    const canvas = canvasRef.current;
    const resolvedProductImage = placement.templateImage || productImage;
    const rawArtworkImage =
      typeof artworkImage === "string" ? artworkImage.trim() : String(artworkImage || "").trim();
    const resolvedArtworkImage = artworkDisabled ? "" : rawArtworkImage;
    if (!canvas || !resolvedProductImage) return;

    let cancelled = false;
    setReady(false);
    const logicalWidth = 600;
    const logicalHeight = Math.round(logicalWidth / placement.canvasAspectRatio);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = Math.round(logicalWidth * dpr);
    canvas.height = Math.round(logicalHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, logicalWidth, logicalHeight);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const render = async () => {
      let artwork: HTMLImageElement | null = null;
      try {
        const [product, loadedArtwork] = await Promise.all([
          loadImage(resolvedProductImage),
          // Load artwork via blob fetch so transparent PNGs keep their alpha.
          resolvedArtworkImage ? loadImage(resolvedArtworkImage, true) : Promise.resolve(null),
        ]);
        artwork = loadedArtwork;
        if (cancelled) {
          releaseArtworkObjectUrl(artwork);
          return;
        }

        const fullCanvas = { x: 0, y: 0, width: logicalWidth, height: logicalHeight };

        if (artwork && placement.artworkLayer === "below-product") {
          const productRect = drawRectForFit(product, fullCanvas, placement.productFit);
          drawArtwork(
            ctx,
            artwork,
            placement,
            resolveArtworkReference(product, productRect, placement, fullCanvas)
          );
          ctx.save();
          ctx.globalCompositeOperation = placement.productCompositeOperation;
          drawImage(ctx, product, fullCanvas, placement.productFit);
          ctx.restore();
        } else {
          const productRect = drawImage(ctx, product, fullCanvas, placement.productFit);
          if (artwork) {
            drawArtwork(
              ctx,
              artwork,
              placement,
              resolveArtworkReference(product, productRect, placement, fullCanvas)
            );
          }
        }

        releaseArtworkObjectUrl(artwork);

        if (!cancelled) setReady(true);

        if (!exportResult && !onRendered) return;

        try {
          const dataUrl = canvas.toDataURL("image/png");
          if (exportResult && itemKey) {
            dispatch(setFinalCanvasImage({ key: itemKey, image: dataUrl }));
          }
          onRendered?.(dataUrl);
        } catch (error) {
          console.warn("Artwork preview rendered but could not be exported.", error);
          onRendered?.(null);
        }
      } catch (error) {
        releaseArtworkObjectUrl(artwork);
        if (!cancelled) {
          setReady(true);
          console.error("Failed to render product artwork preview.", error);
          onRendered?.(null);
        }
      }
    };

    void render();
    return () => {
      cancelled = true;
    };
  }, [
    artworkDisabled,
    artworkImage,
    dispatch,
    exportResult,
    inView,
    itemKey,
    onRendered,
    placement,
    productImage,
  ]);

  return (
    <div
      ref={wrapRef}
      className={`productArtworkPreviewWrap ${ready ? "is-loaded" : "is-shimmer"}${
        className ? ` ${className}` : ""
      }`}
      style={style}
    >
      {inView ? (
        <canvas
          ref={canvasRef}
          className={`productArtworkPreviewCanvas${ready ? " is-visible" : ""}`}
          style={{
            display: "block",
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            maxHeight: 230,
            margin: "0 auto",
            borderRadius: 8,
            objectFit: "contain",
          }}
        />
      ) : null}
    </div>
  );
};

export default ProductArtworkPreview;

